import React, { useMemo, useCallback } from 'react'; // Import useMemo, useCallback
import { useReactFlow, Node } from 'reactflow'; // Import useReactFlow, Node
import { v4 as uuidv4 } from 'uuid'; // Import uuid
import useStore from '../../../store/useStore'; // Adjust path
import { CustomNode, AnyNodeData } from '../../../types'; // Adjust path
import { configBoxes } from '../../../node-types'; // Adjust path

interface ProjectConfigRendererProps {
  node: CustomNode;
  isEmbedded: boolean; // Pass down necessary props
  getPortalRoot: () => HTMLElement | null; // Pass down portal getter
}

const ProjectConfigRenderer: React.FC<ProjectConfigRendererProps> = ({ node, isEmbedded, getPortalRoot }) => {
  const reactFlowInstance = useReactFlow(); // Get full instance
  const { getNode, setNodes, getEdges } = reactFlowInstance; // Destructure needed methods
  // FIX: Get addNode and recalculateClusterLatencies from the store
  const { clusterLatencies, addNode, recalculateClusterLatencies } = useStore((state) => ({
    clusterLatencies: state.clusterLatencies,
    addNode: state.addNode,
    recalculateClusterLatencies: state.recalculateClusterLatencies,
  }));

  // --- Start: Moved connectedClusterId calculation ---
  const connectedClusterId = useMemo(() => {
    if (!node || node.type !== 'javascript-source') return null; // Only calculate for relevant type

    const edges = getEdges();
    const nodes = useStore.getState().nodes;
    const id = node.id; // Use the node's actual ID

    // Helper function to find a cluster connected through a load balancer
    const findClusterThroughLoadBalancer = (nodeId: string, visited = new Set<string>()): string | null => {
      if (visited.has(nodeId)) return null;
      visited.add(nodeId);
      const outEdges = edges.filter(edge => edge.source === nodeId);
      for (const edge of outEdges) {
        const targetNode = nodes.find(n => n.id === edge.target);
        if (targetNode?.type === 'cluster') return targetNode.id;
        if (targetNode?.type === 'load-balancer') {
          const clusterIdThroughLB = findClusterThroughLoadBalancer(targetNode.id, visited);
          if (clusterIdThroughLB) return clusterIdThroughLB;
        }
      }
      return null;
    };

    // Look for incoming edges from a cluster
    const clusterEdges = edges.filter(edge => edge.target === id);
    for (const edge of clusterEdges) {
      const sourceNode = nodes.find(n => n.id === edge.source);
      if (sourceNode?.type === 'cluster') return sourceNode.id;
    }

    // Look for outgoing edges to a cluster or load balancer
    const outgoingEdges = edges.filter(edge => edge.source === id);
    for (const edge of outgoingEdges) {
      const targetNode = nodes.find(n => n.id === edge.target);
      if (targetNode?.type === 'cluster') return targetNode.id;
      if (targetNode?.type === 'load-balancer') {
        const clusterIdThroughLB = findClusterThroughLoadBalancer(targetNode.id);
        if (clusterIdThroughLB) return clusterIdThroughLB;
      }
    }
    return null;
  }, [node, getEdges, clusterLatencies]); // Dependencies for the calculation
  // --- End: Moved connectedClusterId calculation ---


  // Detach handler defined centrally
  const handleDetachFromGroup = () => {
    if (!node || !node.parentId) return; // Ensure node and parentId exist

    const parentNode = getNode(node.parentId);

    // Ensure parent node and necessary properties exist
    if (!parentNode || !parentNode.positionAbsolute || typeof parentNode.width !== 'number') {
      console.error("Parent node data is incomplete for position calculation.");
      // Fallback: Detach without repositioning if parent data is missing
      setNodes((nds) =>
        nds.map((n) => {
          if (n.id === node.id) {
            const { parentId, extent, ...rest } = n;
            return { ...rest, parentId: undefined };
          }
          return n;
        })
      );
      return;
    }

    // Calculate new absolute position to the right of the parent
    const parentRightX = parentNode.positionAbsolute.x + parentNode.width;
    const newX = parentRightX + 20; // 20px offset
    const newY = parentNode.positionAbsolute.y; // Align top edge

    setNodes((nds) =>
      nds.map((n) => {
        if (n.id === node.id) {
          // Create a new object with parentId undefined, new absolute position, and remove extent
          const { parentId, extent, position, ...rest } = n;
          return {
            ...rest,
            parentId: undefined,
            position: { x: newX, y: newY }, // Set new absolute position
          };
        }
        return n;
      })
    );
  };

  const handleUngroup = () => {
    if (!node || node.type !== 'group') return;

    setNodes((nds) => {
      const groupNode = nds.find((n) => n.id === node.id);
      if (!groupNode) return nds;

      const groupX = groupNode.position.x;
      const groupY = groupNode.position.y;

      // Remove the group node and update children
      return nds
        .filter((n) => n.id !== node.id) // Remove the group node
        .map((n) => {
          if (n.parentId === node.id) {
            return {
              ...n,
              parentId: undefined,
              extent: undefined,
              position: {
                x: n.position.x + groupX,
                y: n.position.y + groupY,
              },
            };
          }
          return n;
        });
    });
  };

  // Check if we have a config box for this node type in our new framework
  if (node.type && configBoxes[node.type as keyof typeof configBoxes]) {
    // Prepare props for the specific config box
    const configBoxProps: any = {
      nodeId: node.id, // Pass the actual node ID here
      data: node.data,
      // Pass connectedClusterId if the node type is correct
      ...(node.type === 'javascript-source' && { connectedClusterId }),
      onUpdate: (data: Partial<AnyNodeData>) => {
        if (!node) return;
        // Use the project store directly
        const store = useStore.getState();
        store.updateNode(node.id, data);
        if (node.type === 'cluster') {
          store.recalculateClusterLatencies();
        }
      },
      onDelete: () => {
        if (!node) return;
        const { deleteNode, recalculateClusterLatencies } = useStore.getState();
        deleteNode(node.id);
        if (node.type === 'cluster') {
          recalculateClusterLatencies();
        }
      },
      onDuplicate: useCallback(() => {
        if (!node) return;

        const { addNode, recalculateClusterLatencies } = useStore.getState();
        const allNodes = reactFlowInstance.getNodes(); // Get all current nodes (FIX: Use reactFlowInstance)
        const nodeType = node.type;
        const positionOffset = 50; // How much to offset the duplicated node/group

        if (nodeType === 'group') {
          // --- Group Duplication Logic ---
          const originalGroupId = node.id;
          const newGroupId = `group-${uuidv4()}`;

          // 1. Duplicate the group node itself
          const newGroupNode: CustomNode = {
            ...node,
            id: newGroupId,
            position: { x: node.position.x + positionOffset, y: node.position.y + positionOffset },
            data: { ...node.data, name: `${node.data.name || 'Group'} (Copy)` },
            selected: false, // Ensure duplicated node is not selected initially
          };
          addNode(newGroupNode);

          // 2. Find and duplicate child nodes
          const childNodes = allNodes.filter((n: Node) => n.parentId === originalGroupId); // FIX: Add type annotation

          childNodes.forEach((childNode: Node) => { // FIX: Add type annotation
            const newChildId = `${childNode.type}-${uuidv4()}`; // Generate unique ID for child
            const newChildNode: CustomNode = {
              ...(childNode as CustomNode), // Cast childNode to CustomNode to satisfy type
              id: newChildId,
              parentId: newGroupId, // Set parent to the NEW group ID
              position: { ...childNode.position }, // Keep relative position within the group
              data: { ...childNode.data }, // Deep copy data if necessary, shallow for now
              selected: false, // Ensure duplicated child is not selected
              // Ensure extent is set correctly for children of groups
              extent: 'parent',
            };
            addNode(newChildNode);
          });

        } else {
          // --- Default Duplication Logic (for non-group nodes) ---
          const newId = (() => {
            // Keep existing ID generation for non-groups, but prefer uuid for robustness if possible
            if (nodeType === 'destination' && node.id.includes('-')) {
              const uuidMatch = node.id.match(/destination-([a-f0-9-]+)/);
              if (uuidMatch && uuidMatch[1]) {
                // Append a unique part instead of just timestamp for better collision avoidance
                return `destination-${uuidMatch[1]}-${uuidv4().substring(0, 8)}`;
              }
            }
            // Fallback for other types or if destination pattern doesn't match
            return `${nodeType}-${uuidv4()}`;
          })();

          const newNode: CustomNode = {
            ...node,
            id: newId,
            position: { x: node.position.x + positionOffset, y: node.position.y + positionOffset },
            // Ensure data is copied, modify label/name if applicable
            // FIX: Provide default empty string if label/name is undefined
            data: { ...node.data, label: `${node.data.label || ''} (Copy)`, name: `${node.data.name || ''} (Copy)` },
            selected: false, // Ensure duplicated node is not selected initially
          };
          addNode(newNode); // FIX: addNode is already destructured

          // Recalculate latencies only if the duplicated node was a cluster
          if (node.type === 'cluster') {
            recalculateClusterLatencies();
          }
        }
        // Note: Edge duplication within the group is not handled here yet.
      }, [node, reactFlowInstance, addNode, recalculateClusterLatencies]), // Dependencies are now correctly sourced
      // Conditionally add the detach handler for child nodes
      ...(node.parentId && { onDetachFromGroup: handleDetachFromGroup }),
      // Add ungroup handler for group nodes
      ...(node.type === 'group' && { onDetachFromGroup: handleUngroup }),
      isAdvancedMode: true, // Assuming this is always true for project config
      isDisabled: isEmbedded,
      portalRoot: getPortalRoot(),
    };

    return React.createElement(configBoxes[node.type as keyof typeof configBoxes] as React.ComponentType<any>, configBoxProps);
  } else {
    // Show a message for node types that don't have a config box yet
    return (
      <div className="text-sm text-gray-500 dark:text-gray-400">
        Configuration for {node.type} nodes coming soon
      </div>
    );
  }
};

export default ProjectConfigRenderer;
